import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '@app/core/config';
import { UtilsService } from '@app/core/services';
import { Observable, map } from 'rxjs';

import { WalletType } from '@app/views/pages/wallets/types';
import { ThirdPartyWalletData, WalletSelectList, Wallets } from '../models';
import { BuySellServiceType, SpecialWalletType, TransactionType } from '../types';
import { IDashboradPointWalletsModel } from '../models/dashboard-point-wallets.model';

@Injectable({
	providedIn: 'root'
})
export class WalletPublicService {

	exchangeWalletList: any;
	constructor(
		@Inject(APP_CONFIG) private appConfig: IAppConfig,
		private httpClient: HttpClient,
		private utilsService: UtilsService
	) { }


	private categorizeWallets(flatWallets: any): Wallets {
		let wallets: Wallets = {
			fiat: {
				enable: true,
				stateMessage: null,
				list: []
			},
			crypto: {
				enable: flatWallets.cryptoIsEnable,
				stateMessage: flatWallets.cryptoDisableMessage,
				list: []
			},
			currencyRelations: flatWallets.currencyRelations
		}

		flatWallets.wallets.forEach(el => {
			if (el.currencyType === WalletType.Fiat) {
				wallets.fiat.list.push(el);
			} else {
				wallets.crypto.list.push(el);
			}
		})
		return wallets;

	}

	private categorizeWalletsWithRelations(flatWallets: any): Wallets {
		let wallets: Wallets = {
			fiat: {
				enable: true,
				stateMessage: null,
				list: []
			},
			crypto: {
				enable: flatWallets.cryptoIsEnable,
				stateMessage: flatWallets.cryptoDisableMessage,
				list: []
			},
			currencyRelations: flatWallets.currencyRelations
		}

		flatWallets.wallets.forEach(el => {
			if (!wallets.currencyRelations[el.currencyId]) {
				return;
			}
			if (el.currencyType === WalletType.Fiat) {
				wallets.fiat.list.push(el);
			} else {
				wallets.crypto.list.push(el);
			}
		})
		return wallets;

	}

	getWalletListForTransaction(transactionType: TransactionType, withCategorization: boolean = true): Observable<Wallets | any> {
		return this.httpClient
			.get<Wallets>(`${this.appConfig.apiEndpoint}/new-core/wallets/by-transaction-type/${transactionType}/with-meta`)
			.pipe(map(
				response => {
					if (withCategorization) {
						return this.categorizeWallets(response['data']);
					}
					return this.utilsService.handleResponse(response);
				}
			));
	}

	getPublicList(withCategorization: boolean = true, sortType?: number): Observable<Wallets | IDashboradPointWalletsModel[]> {
		let url = `${this.appConfig.apiEndpoint}/new-core/wallets`;
		let params = new HttpParams();
		if (sortType) {
			params = params.append('sortType', sortType);
		}
		if (withCategorization) {
			url = `${this.appConfig.apiEndpoint}/new-core/wallets/select/with-meta`;
		}

		return this.httpClient
			.get<Wallets>(url, { params })
			.pipe(map(
				response => {
					if (withCategorization) {
						return this.categorizeWallets(response['data']);
					}
					return this.utilsService.handleResponse(response);
				}
			));
	}

	getWalletListHeader(): Observable<Wallets> {
		return this.httpClient
			.get<Wallets>(`${this.appConfig.apiEndpoint}/v3/wallet/headerSelectList`)
			.pipe(map(
				response => {
					return this.categorizeWallets(response['data'])
				}
			));
	}

	getWalletForTransactionsFilter(): Observable<Wallets> {
		return this.httpClient
			.get<Wallets>(`${this.appConfig.apiEndpoint}/new-core/wallets/select`)
			.pipe(map(
				response => {
					return this.categorizeWallets({ wallets: response['data'] });
				}
			));
	}


	getInternalTransferList(withoutContext: boolean = false): Observable<Wallets> {
		let url = `${this.appConfig.apiEndpoint}/new-core/wallets/select/with-meta?context=0`;
		
		if(withoutContext){
			url = `${this.appConfig.apiEndpoint}/new-core/wallets/select/with-meta`;
		}
		
		return this.httpClient
			.get<Wallets>(url)
			.pipe(map(
				response => {
					return this.categorizeWallets(response['data']);
				}
			));
	}


	getExchangeWalletList(): Observable<Wallets> {
		return this.httpClient
			.get<Wallets>(`${this.appConfig.apiEndpoint}/new-core/wallets/select/with-meta?context=1`)
			.pipe(map(
				response => {
					this.exchangeWalletList = this.categorizeWallets(response['data']);
					return this.categorizeWalletsWithRelations(response['data']);
				}
			));
	}

	getStoredExchangeWalletList(): Wallets {
		return this.exchangeWalletList;
	}

	getFriendList(friendshipId: string, fromWallet: string): Observable<Wallets> {
		let url = `${this.appConfig.apiEndpoint}/new-core/wallets/by-friendship/${friendshipId}/with-meta`;

		let params = new HttpParams();
		if (fromWallet) {
			params = params.append('fromWalletId', fromWallet);
		}

		return this.httpClient
			.get<Wallets>(url, { params })
			.pipe(map(response => {
				return this.categorizeWallets(response['data'])
			}));
	}

	// getBuySellServiceList(serviceId: number, type: BuySellServiceType): Observable<WalletSelectList[]> {

	// 	let serviceType = 'SellService';

	// 	if (type === BuySellServiceType.Buy) {
	// 		serviceType = 'BuyService';
	// 	}

	// 	return this.httpClient
	// 		.get<WalletSelectList[]>(`${this.appConfig.apiEndpoint}/v2/wallet/SelectList/${serviceType}/${serviceId}`)
	// 		.pipe(map(this.utilsService.handleResponse));
	// }

	// getThirdPartyWalletData(accountnumber: string, transactionType: SpecialWalletType): Observable<ThirdPartyWalletData> {
	// 	const url = `${this.appConfig.apiEndpoint}/v2/Wallet/ThirdParty/${accountnumber}/${transactionType}`;

	// 	return this.httpClient
	// 		.get<ThirdPartyWalletData[]>(url)
	// 		.pipe(map(this.utilsService.handleResponse));
	// }

	getSendThirdPartyWalletData(walletId: string, accountnumber: string): Observable<ThirdPartyWalletData> {
		const url = `${this.appConfig.apiEndpoint}/new-core/wallets/send-info?fromWalletId=${walletId}&toAccountNumber=${accountnumber}`;

		return this.httpClient
			.get<ThirdPartyWalletData[]>(url)
			.pipe(map(this.utilsService.handleResponse));
	}

	getSendMoneyCurrencyThirdPartyWalletData(accountNumber: string): Observable<ThirdPartyWalletData> {
		const url = `${this.appConfig.apiEndpoint}/new-core/wallets/by-account-number/${accountNumber}/deposit-info`;

		return this.httpClient
			.get<ThirdPartyWalletData[]>(url)
			.pipe(map(this.utilsService.handleResponse));
	}

	getThirdPartyWalletMoreInfo(accountNumber: string, transactionType: SpecialWalletType): Observable<string> {
		const url = `${this.appConfig.apiEndpoint}/new-core/wallets/by-account-number/${accountNumber}/account-info?transactionType=${transactionType}`;
		return this.httpClient
			.get<string>(url)
			.pipe(map(
				res => {
					return res['data']['moreInfo'];
				}
			));
	}

	getForDepositEMoney(serviceId: number): Observable<Wallets> {
		const url = `${this.appConfig.apiEndpoint}/new-core/wallets/by-service/${serviceId}/sell-wallets/with-meta`;
		return this.httpClient
			.get<Wallets>(url)
			.pipe(map(
				response => {
					return this.categorizeWallets(response['data']);
				}
			));
	}

	getForWithdrawalEMoney(serviceId: number): Observable<Wallets> {
		const url = `${this.appConfig.apiEndpoint}/new-core/wallets/by-service/${serviceId}/buy-wallets/with-meta`;
		return this.httpClient
			.get<Wallets>(url)
			.pipe(map(
				response => {
					return this.categorizeWallets(response['data']);
				}
			));
	}

	walletsToSelect2(wallets: Wallets): any {
		const crypto = {
			name: 'WALLET.CRYPTO_WALLETS',
			children: []
		};
		const fiat = {
			name: 'WALLET.FIAT_WALLETS',
			children: []
		};

		const eMoney = {
			name: 'WALLET.EMONEY_WALLETS',
			children: []
		};

		// fill crypto wallet list
		if (wallets['crypto']) {
			wallets['crypto'].list.forEach(element => {
				const item = {
					name: element.accountNumber,
					value: element.walletId,
				};
				crypto.children.push(item);
			});
		}
		// fill Fiat wallet list
		if (wallets['fiat']) {
			wallets['fiat'].list.forEach(element => {
				const item = {
					name: element.accountNumber,
					value: element.walletId,

				};
				fiat.children.push(item);

			});
		}

		const walletsSelect2 = [crypto, eMoney, fiat];
		return walletsSelect2;
	}
}
